/**
 * The clock along a sheet's foot: a hairline that fills while something stays
 * on screen, so a player sees how long it has before it goes by itself. It is
 * a clock, not progress, so it fills in a quiet ink and never the gold: the
 * strong line on slate, the board's dim lavender on a night board
 * (`onNight`).
 *
 *   <FootClock key={line} durationMs={6000} paused={covered} onDone={dismiss} />
 *
 * It keeps the time as well as drawing it: `onDone` is called once, when it
 * runs out, and `paused` holds both the hairline and the time where they are —
 * a moment drawn under another keeps its seconds for when it shows again. Its
 * length is set as it mounts, so a new clock is a new element: key it by what
 * it times. A clock kept elsewhere — the server's, for a phase the player came
 * into late — is drawn from where it stands: `elapsedMs` of it has run as it
 * mounts, and the hairline starts that far along.
 *
 * It lies along the bottom edge of the box it is positioned against, which
 * clips its corners and does not scroll — a sheet, a night board. Where what
 * the box says may be taller than the window, the words scroll in a box of
 * their own inside it (NightBoard's moment does this), or the clock would ride
 * up with them. It is drawn for the eye alone and hidden from assistive
 * technology: what goes by itself says so in words.
 *
 * Its fill is time running out rather than motion, so it runs under reduced
 * motion too, and says so (`data-keeps-time`): the screens walk's
 * reduced-motion pass lets what keeps time run.
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

import styles from './foot-clock.module.css';

export interface FootClockProps {
  /** How long it runs, in milliseconds, from its start. */
  durationMs: number;
  /** How much of it has run as it mounts: a clock kept elsewhere, drawn from where it stands. */
  elapsedMs?: number;
  /** Holds the hairline and the time where they are. */
  paused?: boolean;
  /** Called once, when the time runs out. */
  onDone?: () => void;
  /** On a night board: the board's lavender rather than the slate's lines. */
  onNight?: boolean;
}

export function FootClock({ durationMs, elapsedMs = 0, paused = false, onDone, onNight = false }: FootClockProps) {
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  });

  // The time left, carried across pauses, and whether it has run out.
  const left = useRef(Math.max(0, durationMs - elapsedMs));
  const finished = useRef(false);
  useEffect(() => {
    if (paused || finished.current) return;
    const started = performance.now();
    const timer = window.setTimeout(() => {
      finished.current = true;
      done.current?.();
    }, left.current);
    return () => {
      window.clearTimeout(timer);
      left.current = Math.max(0, left.current - (performance.now() - started));
    };
  }, [paused]);

  // Already run: the fill starts that far along, as a negative delay starts an animation.
  const run = Math.min(Math.max(0, elapsedMs), durationMs);
  return (
    <div
      className={[styles.clock, onNight ? styles.onNight : '', paused ? styles.paused : ''].filter(Boolean).join(' ')}
      style={{ '--clock-duration': `${durationMs}ms`, '--clock-run': `${-run}ms` } as CSSProperties}
      aria-hidden="true"
      data-keeps-time
    />
  );
}
